import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Add, ReceiptLongOutlined } from '@mui/icons-material';
import { cn } from '@utils/cn';
import { useTranslation } from '@hooks/useTranslation';
import { Button } from '@components/atoms/Button';
import { Modal } from '@components/molecules/Modal';
import { Pagination } from '@components/molecules/Pagination';
import ReactSelect from '@components/atoms/ReactSelect';
import { InvoicesFilterBar } from '@components/organisms/InvoicesFilterBar';
import { InvoicesAdminTable } from '@components/organisms/InvoicesAdminTable';
import { PendingInvoicesTable } from '@components/organisms/PendingInvoicesTable';
import { InvoicePreviewDrawer } from '@components/organisms/InvoicePreviewDrawer';
import { InvoiceManagerSlider } from '@components/organisms/InvoiceManagerSlider';
import { ConnectPaymentAccountModal } from '@components/organisms/ConnectPaymentAccountModal';
import {
  useGetPaginatedInvoices,
  useRefreshInvoiceStatus,
  useResendInvoiceEmail,
  useVoidInvoice,
} from '@hooks/api/invoice';
import { useMyPaymentAccount } from '@hooks/api/merchantPaymentAccount';
import { useMyInvoicingPaused } from '@hooks/api/organizations';
import { MerchantAccountStatus } from '@models/merchantPaymentAccount.model';
import { toast } from 'react-toastify';
import { UserRoles } from '@models/user.model';
import { useProjects } from '@hooks/api/projects/useProjects';
import { useTasks } from '@hooks/api/task/useTasks';
import { usePendingInvoicesCount } from '@hooks/api';
import { useProjectFeedByProjects } from '@pages/projects/components/ProjectFeed/hooks';
import {
  ACTIVITY_TYPE_INVOICE_PENDING,
  ActivityResolutionStatus,
} from '@models/activity.model';
import {
  Invoice,
  InvoiceFilterInput,
  InvoiceStatus,
  INVOICING_PAUSED_MESSAGE,
} from '@models/invoice.model';

// 'pending' is sourced from INVOICE_PENDING activities (completed tasks with unbilled
// items), not Invoice records — so it renders a different table and skips the invoice
// status/filter/pagination machinery.
type TabKey = 'all' | 'pending' | 'drafts' | 'sent' | 'paid' | 'voided' | 'errors';

const ITEMS_PER_PAGE = 10;

const TAB_STATUS_MAP: Record<TabKey, InvoiceStatus[] | undefined> = {
  all: undefined,
  pending: undefined,
  drafts: [InvoiceStatus.DRAFT, InvoiceStatus.SENDING],
  sent: [InvoiceStatus.SENT],
  paid: [InvoiceStatus.PAID],
  voided: [InvoiceStatus.VOIDED],
  errors: [InvoiceStatus.SEND_FAILED],
};

const InvoicesPage: React.FC = () => {
  const tActionMessages = useTranslation('actionMessages');
  const tInvoices = useTranslation('invoices');
  // Sprint 31, develop run 2 (priority 2, Invoices sweep — mobile's structurally identical
  // `TransactionsScreen`/`InvoicesScreen` counterpart named in the same sweep). Moved inside
  // the component: a `useTranslation` result cannot be read from a module-level constant.
  const TABS: Array<{ key: TabKey; label: string }> = [
    { key: 'all', label: tInvoices.tabAll },
    { key: 'pending', label: tInvoices.tabPending },
    { key: 'drafts', label: tInvoices.tabDrafts },
    { key: 'sent', label: tInvoices.tabSent },
    { key: 'paid', label: tInvoices.tabPaid },
    { key: 'voided', label: tInvoices.tabVoided },
    { key: 'errors', label: tInvoices.tabErrors },
  ];
  const organizationName = useSelector(
    (state: any) => state?.organization?.name as string | null | undefined,
  );
  const role = useSelector(
    (state: any) => state?.user?.role as string | null | undefined,
  );
  const isOwner = role === UserRoles.OWNER;

  // Whether the org has connected its NMI keys. Until that's set up, invoices
  // can't actually be sent, so we surface a "Setup Invoicing" entry point.
  const paymentAccount = useMyPaymentAccount();
  const isInvoicingSetUp =
    paymentAccount.data?.status === MerchantAccountStatus.CONNECTED;
  // Only decide once the account query has actually resolved. `isSuccess` is false
  // while the query is disabled (no organizationId yet) or still loading, so an
  // already-connected owner never sees the button flash on during rehydration.
  const showSetupButton = paymentAccount.isSuccess && !isInvoicingSetUp;
  const [showConnectModal, setShowConnectModal] = useState(false);

  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [filter, setFilter] = useState<InvoiceFilterInput>({});
  const [page, setPage] = useState(0);

  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [voidingInvoice, setVoidingInvoice] = useState<Invoice | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [resendingInvoice, setResendingInvoice] = useState<Invoice | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resendError, setResendError] = useState<string | null>(null);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [creatorProjectId, setCreatorProjectId] = useState<string | null>(null);
  const [managerProjectId, setManagerProjectId] = useState<string | null>(null);

  const { data: projects = [] } = useProjects();
  const isPendingTab = activeTab === 'pending';

  const projectsIds = useMemo(
    () =>
      projects
        .map((p) => p.projectId)
        .filter((id): id is string => Boolean(id)),
    [projects],
  );

  // Total ACTIVE pending-invoice count across all projects — always fetched so the tab
  // badge stays live regardless of the active tab (mirrors the sidebar badge).
  const { data: pendingCount = 0 } = usePendingInvoicesCount(projectsIds);

  // On the Pending tab the Project filter narrows the feed; with no selection we pass all
  // org projects. Other tabs disable the feed entirely by passing an empty id list.
  const pendingFeedProjectIds = useMemo(() => {
    if (!isPendingTab) return [];
    return filter.projectIds && filter.projectIds.length > 0
      ? filter.projectIds
      : projectsIds;
  }, [isPendingTab, filter.projectIds, projectsIds]);

  // Pending tab: list ACTIVE INVOICE_PENDING activities (same data as the dashboard feed),
  // not Invoice records. Fetched only while the tab is active via the shared project feed.
  const {
    activities: pendingActivities,
    paginationActions: pendingPagination,
    totalCount: pendingTotal,
  } = useProjectFeedByProjects({
    projectsId: pendingFeedProjectIds,
    types: [ACTIVITY_TYPE_INVOICE_PENDING],
    resolutionStatus: ActivityResolutionStatus.ACTIVE,
  });

  // Task filter is only enabled with exactly one project selected — the picker fetches
  // tasks for that project. Multi-project task filtering would require N parallel queries;
  // we leave that to a future enhancement.
  const singleProjectId =
    filter.projectIds && filter.projectIds.length === 1 ? filter.projectIds[0] : undefined;
  const { tasks, loading: tasksLoading } = useTasks(singleProjectId);

  const taskOptions = useMemo(
    () =>
      tasks
        .filter((t) => t.taskId)
        .map((t) => ({ taskId: t.taskId as string, name: t.name })),
    [tasks],
  );

  const projectOptions = useMemo(
    () =>
      projects
        .filter((p) => p.projectId)
        .map((p) => ({ value: p.projectId as string, label: p.name })),
    [projects],
  );

  // Build the effective filter the resolver sees. Tabs override the user-picked status
  // when active; this keeps the two controls coherent without making the user juggle them.
  const effectiveFilter = useMemo<InvoiceFilterInput>(() => {
    const next = { ...filter };
    const tabStatuses = TAB_STATUS_MAP[activeTab];
    if (tabStatuses) {
      next.statuses = tabStatuses;
    }
    return next;
  }, [filter, activeTab]);

  // Reset to page 0 whenever the effective filter changes.
  useEffect(() => {
    setPage(0);
  }, [effectiveFilter]);

  const { data, isLoading } = useGetPaginatedInvoices(
    {
      page,
      limit: ITEMS_PER_PAGE,
      filter: effectiveFilter,
    },
    { enabled: !isPendingTab },
  );

  const invoices = data?.invoices ?? [];
  const total = data?.count ?? 0;

  const { data: invoicingPaused } = useMyInvoicingPaused();
  const voidInvoice = useVoidInvoice();
  const resendInvoice = useResendInvoiceEmail();
  const refreshStatus = useRefreshInvoiceStatus();

  const handleView = (invoice: Invoice) => {
    setPreviewInvoice(invoice);
    if (invoice.status === InvoiceStatus.SENT) {
      refreshStatus.mutate(invoice.invoiceId, {
        onSuccess: (refreshed) => {
          if (!refreshed) return;
          setPreviewInvoice((cur) =>
            cur && cur.invoiceId === refreshed.invoiceId ? refreshed : cur,
          );
        },
      });
    }
  };

  const handleStartResend = async (invoice: Invoice) => {
    // Resending reaches the payment provider, so a super-admin pause blocks it
    // server-side. Say so plainly instead of surfacing the raw server error.
    if (invoicingPaused) {
      toast.info(INVOICING_PAUSED_MESSAGE);
      return;
    }

    // Just-in-time payment-onboarding gate: resending re-sends the invoice email via NMI,
    // which needs the org's connected merchant key. If the org hasn't connected one yet,
    // route to the setup modal instead of letting the resend fail server-side.
    let account = paymentAccount.data;
    if (!account && paymentAccount.isLoading) {
      try {
        const result = await paymentAccount.refetch();
        account = result.data;
      } catch {
        // fall through — treated as not connected below
      }
    }
    if (!account) {
      toast.info(tActionMessages.connectPaymentAccountToResend);
      setShowConnectModal(true);
      return;
    }

    setResendingInvoice(invoice);
    setResendEmail(invoice.recipientEmail || '');
    setResendError(null);
  };
  const handleConfirmResend = () => {
    if (!resendingInvoice) return;
    const trimmed = resendEmail.trim();
    if (!trimmed) {
      setResendError('Please enter a recipient email.');
      return;
    }
    resendInvoice.mutate(
      { invoiceId: resendingInvoice.invoiceId, recipientEmail: trimmed },
      {
        onSuccess: () => {
          setResendingInvoice(null);
          setResendEmail('');
        },
      },
    );
  };

  const handleStartVoid = (invoice: Invoice) => {
    // Voiding closes the invoice in the payment provider, so a pause blocks it too.
    if (invoicingPaused) {
      toast.info(INVOICING_PAUSED_MESSAGE);
      return;
    }
    setVoidingInvoice(invoice);
    setVoidReason('');
  };
  const handleConfirmVoid = () => {
    if (!voidingInvoice) return;
    voidInvoice.mutate(
      { invoiceId: voidingInvoice.invoiceId, reason: voidReason.trim() || undefined },
      {
        onSuccess: () => {
          setVoidingInvoice(null);
          setVoidReason('');
        },
      },
    );
  };

  const handleStartCreate = () => {
    setCreatorProjectId(null);
    setCreatorOpen(true);
  };
  const handleConfirmCreate = () => {
    if (!creatorProjectId) return;
    setCreatorOpen(false);
    setManagerProjectId(creatorProjectId);
  };

  const managerProject = projects.find((p) => p.projectId === managerProjectId);

  return (
    <div className="flex h-full">
      <div className="flex-1 flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ReceiptLongOutlined
              sx={{ fontSize: 28 }}
              className="text-orange-400 dark:text-orange-300"
            />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invoices</h1>
          </div>
          <div className="flex items-center gap-2">
            {showSetupButton &&
              (isOwner ? (
                <Button variant="secondary" onClick={() => setShowConnectModal(true)}>
                  Setup Invoicing
                </Button>
              ) : (
                // Only the owner holds the org's payment keys, so non-owners get a
                // disabled button explaining who to reach out to.
                <span title="Only the organization owner can set up invoicing. Please contact your owner.">
                  <Button variant="secondary" disabled>
                    Setup Invoicing
                  </Button>
                </span>
              ))}
            <Button leftIcon={<Add sx={{ fontSize: 18 }} />} onClick={handleStartCreate}>
              New invoice
            </Button>
          </div>
        </div>

      {/* Filter bar — stays mounted across tabs. The Pending tab lists activities, so it
          shows a compact Project-only variant instead of vanishing. */}
      <InvoicesFilterBar
        filter={filter}
        onChange={setFilter}
        projects={projects}
        tasks={taskOptions}
        isLoadingTasks={tasksLoading}
        pendingMode={isPendingTab}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200 flex gap-1 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'px-4 py-2 text-sm whitespace-nowrap flex items-center',
              activeTab === tab.key
                ? 'font-medium text-blue-800 border-b-2 border-blue-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700',
            )}>
            {tab.label}
            {/* Amber pending count badge — matches the sidebar Invoices badge; hidden at 0. */}
            {tab.key === 'pending' && pendingCount > 0 && (
              <span className="ml-1.5 bg-amber-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none tabular-nums">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Table — Pending tab lists INVOICE_PENDING activities; others list invoices. */}
      {isPendingTab ? (
        <>
          <PendingInvoicesTable
            activities={pendingActivities}
            isLoading={pendingPagination.isLoading}
          />
          {pendingPagination.hasMore && (
            <div className="flex justify-center pt-2">
              <Button
                variant="secondary"
                onClick={pendingPagination.loadMore}
                loading={pendingPagination.isFetchingMore}>
                Load more ({pendingActivities.length} of {pendingTotal})
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <InvoicesAdminTable
            invoices={invoices}
            projects={projects}
            isLoading={isLoading}
            onView={handleView}
            onResend={handleStartResend}
            onVoid={handleStartVoid}
            onRefreshStatus={(inv) => refreshStatus.mutate(inv.invoiceId)}
          />

          {/* Pagination — uses 1-based page index per molecule convention. */}
          <Pagination
            page={page + 1}
            take={ITEMS_PER_PAGE}
            total={total}
            setPage={(p) => setPage(p - 1)}
          />
        </>
      )}
      </div>

      {/* Invoice preview */}
      <InvoicePreviewDrawer
        isOpen={previewInvoice !== null}
        invoice={previewInvoice}
        organizationName={organizationName || undefined}
        onClose={() => setPreviewInvoice(null)}
      />

      {/* Void modal */}
      <Modal
        isOpen={voidingInvoice !== null}
        onClose={() => setVoidingInvoice(null)}
        title={`Void invoice ${voidingInvoice?.invoiceNumber || ''}`}
        description={
          voidingInvoice
            ? `This will mark invoice ${voidingInvoice.invoiceNumber || voidingInvoice.invoiceId.slice(0, 8)} as voided and close it in NMI. The budget items charged here will be released if voiding from SENT. The recipient will be notified by email.`
            : ''
        }
        size="small"
        handleConfirm={handleConfirmVoid}
        confirmText={voidInvoice.isPending ? 'Voiding…' : 'Void invoice'}
        cancelText="Cancel"
        isLoading={voidInvoice.isPending}>
        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-wide text-gray-500" htmlFor="invoices-page-void-reason">
            Reason <span className="text-gray-400 normal-case">(optional)</span>
          </label>
          <textarea
            id="invoices-page-void-reason"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="e.g., Created in error, client cancelled order…"
            rows={3}
            className="px-3 py-2 border border-gray-300 rounded text-sm bg-white resize-none"
          />
        </div>
      </Modal>

      {/* Resend modal */}
      <Modal
        isOpen={resendingInvoice !== null}
        onClose={() => setResendingInvoice(null)}
        title={`Resend invoice ${resendingInvoice?.invoiceNumber || ''}`}
        description="The recipient will receive the invoice email again at the address below."
        size="small"
        handleConfirm={handleConfirmResend}
        confirmText={resendInvoice.isPending ? 'Sending…' : 'Resend'}
        cancelText="Cancel"
        isLoading={resendInvoice.isPending}>
        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-wide text-gray-500" htmlFor="invoices-page-resend-email">
            Recipient email
          </label>
          <input
            id="invoices-page-resend-email"
            type="email"
            value={resendEmail}
            onChange={(e) => {
              setResendEmail(e.target.value);
              if (resendError) setResendError(null);
            }}
            placeholder="invoices@client.com"
            className={cn(
              'px-3 py-2 border rounded text-sm bg-white',
              resendError ? 'border-red-400' : 'border-gray-300',
            )}
          />
          {resendError && <p className="text-xs text-red-600">{resendError}</p>}
        </div>
      </Modal>

      {/* Create — pick project, then open the existing per-project manager */}
      <Modal
        isOpen={creatorOpen}
        onClose={() => setCreatorOpen(false)}
        title="New invoice"
        description="Pick the project to invoice. You'll be able to add line items, budget charges, taxes and discounts in the next step."
        size="small"
        handleConfirm={handleConfirmCreate}
        confirmText="Continue"
        cancelText="Cancel"
        disableConfirm={!creatorProjectId}>
        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-wide text-gray-500">Project</label>
          <ReactSelect
            options={projectOptions}
            value={projectOptions.find((o) => o.value === creatorProjectId) || null}
            onChange={(v) => {
              const value = v as { value: string } | null;
              setCreatorProjectId(value ? value.value : null);
            }}
            placeholder="Select a project…"
          />
        </div>
      </Modal>

      {/* Per-project invoice manager (reused for the create flow) */}
      {managerProjectId && (
        <InvoiceManagerSlider
          isOpen={true}
          projectId={managerProjectId}
          projectName={managerProject?.name}
          defaultRecipientEmail={managerProject?.client?.email}
          onClose={() => setManagerProjectId(null)}
        />
      )}

      {/* Owner-only invoicing setup (NMI key connection) */}
      <ConnectPaymentAccountModal
        open={showConnectModal}
        onClose={() => setShowConnectModal(false)}
      />
    </div>
  );
};

export default InvoicesPage;
